// =============================================================================
// CreateSpaceDialog - Dialog pour créer un espace
// =============================================================================

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Folder, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSpacesStore } from '@/modules/project/hooks/useSpacesStore';

// Palette de couleurs prédéfinies
const SPACE_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

interface CreateSpaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSpaceDialog({ open, onOpenChange }: CreateSpaceDialogProps) {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { addSpace } = useSpacesStore();
  
  const [name, setName] = useState('');
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentOrganization) {
      toast.error('Aucun workspace sélectionné');
      return;
    }
    
    if (!name.trim() || name.trim().length < 2) {
      toast.error('Le nom doit contenir au moins 2 caractères');
      return;
    }
    if (name.trim().length > 60) {
      toast.error('Le nom ne peut pas dépasser 60 caractères');
      return;
    }

    setIsLoading(true);

    try {
      await addSpace({
        org_id: currentOrganization.id,
        name: name.trim(),
        color,
        created_by: user?.id,
      });

      toast.success('Espace créé avec succès');
      
      // Reset form
      setName('');
      setColor(SPACE_COLORS[0]);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Error creating space:', err);
      toast.error(err.message || 'Erreur lors de la création de l\'espace');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setColor(SPACE_COLORS[0]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div 
              className="flex items-center justify-center h-10 w-10 rounded-xl shadow-lg"
              style={{ backgroundColor: color }}
            >
              <Folder className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                Nouvel espace
              </DialogTitle>
              <DialogDescription className="text-sm">
                Créez un espace pour organiser vos projets
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="space-name">Nom de l'espace *</Label>
            <Input
              id="space-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              placeholder="Ex: Équipe Marketing"
              required
              minLength={2}
              maxLength={60}
              disabled={isLoading}
              autoFocus
            />
            {name.length >= 50 && (
              <p className="text-[11px] text-muted-foreground">{name.length}/60 caractères</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Couleur</Label>
            <div className="flex flex-wrap gap-2">
              {SPACE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !name.trim()}
              style={{ backgroundColor: color }}
              className="text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer l\'espace'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
